import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ChatWindow } from "@/components/ChatWindow";
import { ChevronLeft, Headset, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SupportChat() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const lastTs = useRef(null);

  const load = async (initial = false) => {
    try {
      const params = !initial && lastTs.current ? { after: lastTs.current } : {};
      const { data } = await api.get("/chat/messages", { params });
      if (data.messages.length) {
        lastTs.current = data.messages[data.messages.length - 1].created_at;
        setMessages((prev) => (initial ? data.messages : [...prev, ...data.messages]));
        if (data.messages.some((m) => m.sender_role === "ADMIN")) await api.post("/chat/read").catch(() => {});
      }
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (payload) => {
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", payload);
      lastTs.current = data.created_at;
      setMessages((p) => [...p, data]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="support-chat-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <button onClick={() => nav("/")} className="text-sm text-muted-foreground hover:text-foreground flex items-center" data-testid="chat-back-button">
            <ChevronLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 w-full flex-1 flex flex-col min-h-0 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Headset className="h-5 w-5 text-brand" />
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">Chat with Support</h1>
            <p className="text-xs text-muted-foreground">Replies appear here automatically — no need to refresh.</p>
          </div>
        </div>
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
        ) : (
          <ChatWindow messages={messages} selfRole="CUSTOMER" onSend={send} sending={sending} />
        )}
      </main>
    </div>
  );
}
